import React, { useState, useMemo, useRef } from 'react';
import { Upload, Loader2, CheckCircle2, AlertCircle, PlusCircle, FileText, Sparkles, X, Scale, ScanLine, Camera } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { receptionsAPI, supplierProductsAPI } from '@/lib/api';
import { PhoneCaptureButton } from '@/components/PhoneCapture';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * Generic AI invoice import dialog.
 * - Supplier is selectable.
 * - If supplier is "Carrefour", uses the legacy Carrefour endpoint (preserves
 *   weighed-item logic + on-the-fly SP creation).
 * - Otherwise, uses the generic invoice endpoint (no auto-creation; user must
 *   manually map any unmatched line to an existing supplier_product, which is
 *   then learned as an alias for next time).
 */
export default function InvoiceImportDialog({ open, onClose, sites = [], suppliers = [], onImported }) {
    const [siteId, setSiteId] = useState('');
    const [supplierId, setSupplierId] = useState('');
    const [forceScan, setForceScan] = useState(false);
    const [files, setFiles] = useState([]); // Array<File | {isPhotoGroup, name, size, photos: File[]}>
    const cameraInputRef = useRef(null);

    const addPhotoPage = (photo) => {
        setFiles(prev => {
            const idx = prev.findIndex(f => f.isPhotoGroup);
            if (idx >= 0) {
                const group = prev[idx];
                const photos = [...group.photos, photo];
                const next = [...prev];
                next[idx] = { ...group, photos, name: `Facture photographiée (${photos.length} page${photos.length > 1 ? 's' : ''})`, size: group.size + photo.size };
                return next;
            }
            return [...prev, { isPhotoGroup: true, name: 'Facture photographiée (1 page)', size: photo.size, photos: [photo] }];
        });
    };
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState('');
    const [percent, setPercent] = useState(0);
    // Per-file progress while batch-analyzing multiple PDFs.
    const [fileProgress, setFileProgress] = useState([]); // [{name, status, percent, error?}]
    const [preview, setPreview] = useState(null);
    const [selectedInvoices, setSelectedInvoices] = useState({});
    const [manualMaps, setManualMaps] = useState({}); // `${invIdx}-${itemIdx}` -> sp_id
    const [createFlags, setCreateFlags] = useState({}); // Carrefour-only
    const [vwConfirms, setVwConfirms] = useState({}); // `${invIdx}-${j}` -> poids manuscrit accepté/corrigé
    const [supplierProducts, setSupplierProducts] = useState([]); // for dropdown matching

    const selectedSupplier = useMemo(
        () => suppliers.find(s => s.id === supplierId),
        [supplierId, suppliers]
    );
    const isCarrefour = (selectedSupplier?.name || '').toLowerCase().includes('carrefour');

    const reset = () => {
        setFiles([]);
        setPreview(null);
        setProgress('');
        setPercent(0);
        setFileProgress([]);
        setSelectedInvoices({});
        setManualMaps({});
        setCreateFlags({});
        setVwConfirms({});
        setForceScan(false);
    };

    const handleClose = () => {
        reset();
        setSiteId('');
        setSupplierId('');
        onClose();
    };

    // Apply the Carrefour weighed-item suggestion on a single line:
    // qty 1 + total > 1.5€ → derive qty (kg) and PU from price history.
    const applyWeightCorrection = (invIdx, j) => {
        const it = preview?.invoices?.[invIdx]?.items?.[j];
        if (!it?.weighed_suggestion) return;
        const s = it.weighed_suggestion;
        const newQty = s.quantity_corrected;
        const newPu = s.pu_ref;
        const copy = JSON.parse(JSON.stringify(preview));
        copy.invoices[invIdx].items[j] = {
            ...it,
            quantity: newQty,
            unit_price_ht: newPu,
            total_line_ht: Math.round(newQty * newPu * 100) / 100,
            weighed_suggestion: null,
        };
        setPreview(copy);
    };

    const applyAllWeightCorrections = () => {
        const copy = JSON.parse(JSON.stringify(preview));
        let count = 0;
        copy.invoices.forEach((inv) => inv.items.forEach((it, j) => {
            if (it.weighed_suggestion) {
                const s = it.weighed_suggestion;
                inv.items[j] = {
                    ...it,
                    quantity: s.quantity_corrected,
                    unit_price_ht: s.pu_ref,
                    total_line_ht: Math.round(s.quantity_corrected * s.pu_ref * 100) / 100,
                    weighed_suggestion: null,
                };
                count++;
            }
        }));
        setPreview(copy);
        if (count > 0) toast.success(`${count} ligne(s) corrigée(s)`);
    };

    // Process a single PDF: start the preview job and poll status until done.
    // Returns the extraction result (or throws).
    const analyzeSingleFile = async (singleFile, onProgress) => {
        const fd = new FormData();
        if (singleFile.isPhotoGroup) {
            singleFile.photos.forEach(p => fd.append('photos', p));
        } else {
            fd.append('pdf', singleFile);
        }
        fd.append('site_id', siteId);

        let startRes, jobId, statusFn;
        if (isCarrefour) {
            startRes = await receptionsAPI.importCarrefourPreview(fd);
            jobId = startRes.data.job_id;
            statusFn = receptionsAPI.importCarrefourPreviewStatus;
        } else {
            fd.append('supplier_id', supplierId);
            if (forceScan) fd.append('force_scan', 'true');
            startRes = await receptionsAPI.importInvoicePreview(fd);
            jobId = startRes.data.job_id;
            statusFn = receptionsAPI.importInvoicePreviewStatus;
            if (startRes.data.mode === 'scan') {
                toast.info(`${singleFile.name} : PDF scanné détecté`);
            }
        }
        if (!jobId) throw new Error('Pas de job_id retourné');

        const startTime = Date.now();
        const TIMEOUT_MS = 5 * 60 * 1000;
        while (Date.now() - startTime < TIMEOUT_MS) {
            await new Promise(r => setTimeout(r, 1500));
            const stRes = await statusFn(jobId);
            const st = stRes.data;
            if (st.progress) onProgress?.(st.progress, st.percent || 0);
            if (st.status === 'done') {
                onProgress?.('Terminé', 100);
                return st.result;
            }
            if (st.status === 'error') {
                throw new Error(st.error || 'Erreur inconnue');
            }
        }
        throw new Error("Délai d'extraction dépassé (5 min)");
    };

    const handleAnalyze = async () => {
        if (files.length === 0 || !siteId || !supplierId) {
            toast.error('Choisissez un fournisseur, un site et au moins un PDF');
            return;
        }
        setLoading(true);
        setProgress(`Traitement de ${files.length} fichier(s)…`);
        setPercent(1);
        setFileProgress(files.map(f => ({ name: f.name, status: 'pending', percent: 0 })));

        // Sequential processing: easier to follow + lower memory than parallel.
        const aggregated = { supplier_id: null, supplier_name: '', site_id: siteId, site_name: '', mode: null, invoices: [] };
        let succeeded = 0;
        let failed = 0;
        for (let idx = 0; idx < files.length; idx++) {
            const f = files[idx];
            setFileProgress(prev => prev.map((p, i) => i === idx ? { ...p, status: 'running', percent: 0 } : p));
            setProgress(`Fichier ${idx + 1}/${files.length} — ${f.name}`);
            setPercent(Math.round((idx / files.length) * 100));
            try {
                const result = await analyzeSingleFile(f, (msg, pct) => {
                    setFileProgress(prev => prev.map((p, i) => i === idx ? { ...p, percent: pct } : p));
                    setProgress(`Fichier ${idx + 1}/${files.length} — ${msg}`);
                });
                // Aggregate invoices and remember supplier/site/mode from first result
                if (!aggregated.supplier_id) {
                    aggregated.supplier_id = result.supplier_id;
                    aggregated.supplier_name = result.supplier_name;
                    aggregated.site_name = result.site_name;
                    aggregated.mode = result.mode;
                }
                // Tag invoices with their source filename for clarity in the UI.
                (result.invoices || []).forEach(inv => {
                    aggregated.invoices.push({ ...inv, source_filename: f.name });
                });
                setFileProgress(prev => prev.map((p, i) => i === idx ? { ...p, status: 'done', percent: 100 } : p));
                succeeded++;
            } catch (e) {
                const errMsg = e.response?.data?.detail || e.message || 'Erreur';
                setFileProgress(prev => prev.map((p, i) => i === idx ? { ...p, status: 'error', error: errMsg } : p));
                failed++;
            }
        }

        if (aggregated.invoices.length === 0) {
            toast.error(`Aucune facture extraite (${failed} échec${failed > 1 ? 's' : ''})`);
            setLoading(false);
            setProgress('');
            setPercent(0);
            return;
        }

        setPreview(aggregated);

        // Load supplier products list (generic suppliers only)
        if (!isCarrefour) {
            try {
                const spRes = await supplierProductsAPI.getAll();
                const list = (spRes.data || []).filter(sp => sp.supplier_id === supplierId);
                setSupplierProducts(list);
            } catch {
                setSupplierProducts([]);
            }
        }

        const sel = {};
        aggregated.invoices.forEach((inv, i) => { sel[i] = !inv.already_imported; });
        setSelectedInvoices(sel);

        if (isCarrefour) {
            const flags = {};
            aggregated.invoices.forEach((inv, i) => {
                inv.items.forEach((it, j) => { flags[`${i}-${j}`] = it.match_status === 'new'; });
            });
            setCreateFlags(flags);
        }

        const msg = `${aggregated.invoices.length} facture(s) détectée(s)` + (failed > 0 ? ` — ${failed} fichier(s) en erreur` : '');
        if (failed > 0) toast.warning(msg); else toast.success(msg);
        setLoading(false);
        setProgress('');
        setPercent(0);
        if (succeeded > 0) setFileProgress([]); // hide list once we transition to the preview screen
    };

    const handleConfirm = async () => {
        if (!preview) return;
        if (!isCarrefour) {
            let pendingVW = 0;
            preview.invoices.forEach((inv, invIdx) => {
                if (!selectedInvoices[invIdx]) return;
                inv.items.forEach((it, j) => {
                    const key = `${invIdx}-${j}`;
                    const spId = manualMaps[key] !== undefined ? manualMaps[key] : it.matched_supplier_product_id;
                    if (spId && spById[spId]?.variable_weight && !vwConfirms[key]) pendingVW++;
                });
            });
            if (pendingVW > 0) {
                toast.warning(`Poids variable : acceptez ou corrigez ${pendingVW} poids manuscrit(s) avant d'importer.`);
                return;
            }
        }
        setLoading(true);
        try {
            if (isCarrefour) {
                // Use legacy Carrefour confirm endpoint
                const payload = {
                    supplier_id: preview.supplier_id,
                    site_id: preview.site_id,
                    invoices: preview.invoices
                        .filter((_, i) => selectedInvoices[i])
                        .map((inv) => {
                            const invIdx = preview.invoices.indexOf(inv);
                            return {
                                invoice_number: inv.invoice_number,
                                invoice_date: inv.invoice_date,
                                items: inv.items
                                    .filter((it, j) => it.matched_supplier_product_id || createFlags[`${invIdx}-${j}`])
                                    .map((it) => {
                                        const j = inv.items.indexOf(it);
                                        return {
                                            product_ref: it.product_ref,
                                            description: it.description,
                                            vat_rate: it.vat_rate,
                                            unit_price_ht: it.unit_price_ht,
                                            quantity: it.quantity,
                                            matched_supplier_product_id: it.matched_supplier_product_id || null,
                                            create_new: !it.matched_supplier_product_id && createFlags[`${invIdx}-${j}`],
                                        };
                                    }),
                            };
                        }),
                };
                const res = await receptionsAPI.importCarrefourConfirm(payload);
                toast.success(`${res.data.created_count} réception(s) créée(s), ${res.data.created_products_count} nouveau(x) produit(s)`);
            } else {
                // Generic confirm: include manual maps + flag for alias learning
                const payload = {
                    supplier_id: preview.supplier_id,
                    site_id: preview.site_id,
                    invoices: preview.invoices
                        .filter((_, i) => selectedInvoices[i])
                        .map((inv) => {
                            const invIdx = preview.invoices.indexOf(inv);
                            return {
                                invoice_number: inv.invoice_number,
                                invoice_date: inv.invoice_date,
                                client_name: inv.client_name || null,
                                items: inv.items.map((it, j) => {
                                    const manualSpId = manualMaps[`${invIdx}-${j}`];
                                    const finalSpId = manualSpId || it.matched_supplier_product_id || null;
                                    const wasManual = !!manualSpId || (it.match_status === 'fuzzy' && !!it.matched_supplier_product_id);
                                    return {
                                        product_ref: it.product_ref,
                                        description: it.description,
                                        vat_rate: it.vat_rate,
                                        unit_price_ht: it.unit_price_ht,
                                        quantity: it.quantity,
                                        matched_supplier_product_id: finalSpId,
                                        was_manually_mapped: wasManual && !!finalSpId,
                                    };
                                }).filter(it => !!it.matched_supplier_product_id),
                            };
                        }),
                };
                const res = await receptionsAPI.importInvoiceConfirm(payload);
                const dup = res.data.duplicate_count || 0;
                if (res.data.created_count === 0 && dup > 0) {
                    toast.warning(`Facture(s) déjà importée(s) : ${(res.data.duplicates || []).join(', ')} — aucune réception créée`);
                } else {
                    toast.success(
                        `${res.data.created_count} réception(s) créée(s). ` +
                        `${res.data.learned_aliases} alias appris, ${res.data.skipped_lines} ligne(s) ignorée(s)` +
                        (dup > 0 ? ` — ${dup} doublon(s) ignoré(s)` : '')
                    );
                }
            }
            if (onImported) onImported();
            handleClose();
        } catch (e) {
            if (!e.response) {
                toast.error('Erreur réseau : la validation n\'a pas atteint le serveur. Vérifiez votre connexion et cliquez à nouveau sur Valider.');
            } else {
                toast.error(e.response?.data?.detail || `Erreur d'import (HTTP ${e.response.status})`);
            }
        } finally {
            setLoading(false);
        }
    };

    const statusBadge = (s) => {
        if (s === 'exact') return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold"><CheckCircle2 className="w-3 h-3" />Reconnu</span>;
        if (s === 'fuzzy') return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold"><AlertCircle className="w-3 h-3" />Similaire</span>;
        return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-semibold"><PlusCircle className="w-3 h-3" />Nouveau</span>;
    };

    const selectedCount = Object.values(selectedInvoices).filter(Boolean).length;
    const supplierProductOptions = useMemo(
        () => [...supplierProducts].sort((a, b) =>
            (a.supplier_name_product || a.supplier_ref || '').localeCompare(
                b.supplier_name_product || b.supplier_ref || ''
            )
        ),
        [supplierProducts]
    );
    const spById = useMemo(() => Object.fromEntries(supplierProducts.map(sp => [sp.id, sp])), [supplierProducts]);

    const setItemQty = (invIdx, j, v) => {
        setPreview(prev => {
            const copy = JSON.parse(JSON.stringify(prev));
            const it2 = copy.invoices[invIdx].items[j];
            it2.quantity = parseFloat(v) || 0;
            it2.total_line_ht = Math.round(it2.quantity * (it2.unit_price_ht || 0) * 100) / 100;
            return copy;
        });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="invoice-import-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-orange-500" />
                        Import facture (IA)
                    </DialogTitle>
                    <DialogDescription>
                        Le PDF est analysé par IA et chaque facture devient une réception manuelle distincte.
                        Les correspondances de produits sont apprises automatiquement pour les imports suivants.
                    </DialogDescription>
                </DialogHeader>

                {!preview ? (
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label>Fournisseur *</Label>
                                <Select value={supplierId} onValueChange={setSupplierId}>
                                    <SelectTrigger data-testid="invoice-supplier-select"><SelectValue placeholder="Choisir un fournisseur" /></SelectTrigger>
                                    <SelectContent>
                                        {[...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })).map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label>Site Fuji Express *</Label>
                                <Select value={siteId} onValueChange={setSiteId}>
                                    <SelectTrigger data-testid="invoice-site-select"><SelectValue placeholder="Choisir un site" /></SelectTrigger>
                                    <SelectContent>
                                        {sites.map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label>Facture(s) : PDF ou photo *</Label>
                            <div
                                className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-orange-400 transition-colors"
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const dropped = Array.from(e.dataTransfer.files || []).filter(f => f.name.toLowerCase().endsWith('.pdf'));
                                    if (dropped.length === 0) return;
                                    setFiles(prev => [...prev, ...dropped]);
                                }}
                            >
                                <input
                                    type="file"
                                    accept=".pdf,image/*"
                                    multiple
                                    className="hidden"
                                    id="invoice-pdf-input"
                                    onChange={(e) => {
                                        const picked = Array.from(e.target.files || []);
                                        picked.forEach(f => {
                                            if (f.type.startsWith('image/')) addPhotoPage(f);
                                            else setFiles(prev => [...prev, f]);
                                        });
                                        e.target.value = ''; // allow re-selecting the same files later
                                    }}
                                />
                                <label htmlFor="invoice-pdf-input" className="cursor-pointer flex flex-col items-center gap-1">
                                    <Upload className="w-8 h-8 text-slate-400" />
                                    <span className="text-sm text-slate-500">
                                        Cliquer ou glisser des PDF ici{files.length > 0 ? ' (ajout)' : ''}
                                    </span>
                                </label>
                            </div>
                            {!isCarrefour && (
                                <>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full border-orange-300 text-orange-600 hover:bg-orange-50"
                                        onClick={() => cameraInputRef.current?.click()}
                                        data-testid="invoice-import-camera-btn"
                                    >
                                        <Camera className="w-4 h-4 mr-2" />
                                        {files.some(f => f.isPhotoGroup) ? 'Photographier une page de plus' : 'Photographier une facture papier'}
                                    </Button>
                                    <input
                                        ref={cameraInputRef}
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        className="hidden"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) addPhotoPage(f);
                                            e.target.value = '';
                                        }}
                                        data-testid="invoice-import-camera-input"
                                    />
                                    <PhoneCaptureButton
                                        className="w-full"
                                        label="Prendre avec le téléphone"
                                        testId="invoice-import-phone-btn"
                                        onPhotos={(fs) => fs.forEach(addPhotoPage)}
                                    />
                                </>
                            )}
                            {files.length > 0 && (
                                <div className="border rounded-lg divide-y" data-testid="selected-files-list">
                                    {files.map((f, idx) => {
                                        const fp = fileProgress[idx];
                                        return (
                                            <div key={`${f.name}-${idx}`} className="flex items-center gap-2 px-2 py-1 text-xs">
                                                {f.isPhotoGroup
                                                    ? <Camera className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                                                    : <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                                                <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                                                <span className="text-[10px] text-slate-400 tabular-nums">{(f.size / 1024).toFixed(0)} ko</span>
                                                {fp && fp.status === 'running' && (
                                                    <span className="flex items-center gap-1 text-orange-600">
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                        {fp.percent}%
                                                    </span>
                                                )}
                                                {fp && fp.status === 'done' && (
                                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                                )}
                                                {fp && fp.status === 'error' && (
                                                    <span className="flex items-center gap-1 text-red-600" title={fp.error}>
                                                        <AlertCircle className="w-3.5 h-3.5" />
                                                    </span>
                                                )}
                                                {!loading && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                                                        className="p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-red-600"
                                                        title="Retirer"
                                                        data-testid={`remove-file-${idx}`}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        {!isCarrefour && (
                            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                                <Checkbox
                                    checked={forceScan}
                                    onCheckedChange={(v) => setForceScan(!!v)}
                                    data-testid="invoice-force-scan"
                                />
                                <ScanLine className="w-4 h-4 text-slate-400" />
                                <span>Forcer le mode <strong>scan</strong> (PDF photographié / mauvaise qualité)</span>
                            </label>
                        )}
                        {loading && (
                            <div className="space-y-2 px-3 py-3 bg-orange-50 border border-orange-200 rounded-lg" data-testid="invoice-import-progress">
                                <div className="flex items-center gap-2 text-sm text-orange-800">
                                    <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                                    <span className="flex-1 truncate">{progress || 'Traitement en cours...'}</span>
                                    <span className="font-bold tabular-nums text-orange-700">{percent}%</span>
                                </div>
                                <div className="h-2 w-full bg-orange-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-300 ease-out"
                                        style={{ width: `${Math.max(percent, 2)}%` }}
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={handleClose}>Annuler</Button>
                            <Button onClick={handleAnalyze} disabled={files.length === 0 || !siteId || !supplierId || loading} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="analyze-pdf-btn">
                                {loading
                                    ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analyse IA…</>
                                    : <><Sparkles className="w-4 h-4 mr-2" />Analyser {files.length > 1 ? `${files.length} PDF` : ''}</>}
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <>
                        <div className="flex-shrink-0 flex items-center gap-3 py-2 border-b text-sm">
                            <span className="font-semibold">{preview.supplier_name}</span>
                            <span className="text-slate-400">→</span>
                            <span className="font-semibold">{preview.site_name}</span>
                            {preview.mode && (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${preview.mode === 'scan' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {preview.mode === 'scan' ? 'Mode scan' : 'PDF natif'}
                                </span>
                            )}
                            <div className="ml-auto flex items-center gap-2">
                                <span className="text-slate-500" data-testid="invoice-selected-count">
                                    {selectedCount} / {preview.invoices.length} sélectionnées
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => {
                                        // Toggle: if all are selected, unselect all; else select all not-already-imported.
                                        const allSelected = preview.invoices.every((_, i) => selectedInvoices[i]);
                                        const next = {};
                                        preview.invoices.forEach((inv, i) => {
                                            next[i] = allSelected ? false : !inv.already_imported;
                                        });
                                        setSelectedInvoices(next);
                                    }}
                                    data-testid="toggle-select-all-invoices"
                                >
                                    {preview.invoices.every((_, i) => selectedInvoices[i])
                                        ? 'Tout décocher'
                                        : 'Tout cocher'}
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-3 py-2 min-w-0">
                            {preview.invoices.map((inv, invIdx) => (
                                <div key={invIdx} className={`border rounded-lg min-w-0 ${selectedInvoices[invIdx] ? 'border-emerald-200' : 'border-slate-200 opacity-60'}`}>
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 border-b">
                                        <Checkbox
                                            checked={!!selectedInvoices[invIdx]}
                                            onCheckedChange={(v) => setSelectedInvoices(prev => ({ ...prev, [invIdx]: !!v }))}
                                            data-testid={`select-invoice-${invIdx}`}
                                        />
                                        <FileText className="w-4 h-4 text-slate-500" />
                                        <span className="font-mono font-semibold">{inv.invoice_number}</span>
                                        <span className="text-slate-500">{inv.invoice_date}</span>
                                        {inv.source_filename && files.length > 1 && (
                                            <span className="text-[10px] text-slate-400 italic truncate max-w-[200px]" title={inv.source_filename}>
                                                ({inv.source_filename})
                                            </span>
                                        )}
                                        <span className="ml-auto font-bold text-emerald-600">{formatCurrency(inv.total_ht)} HT</span>
                                        {[0, 2.1, 8.5].map(rate => inv.items.some(it => it.vat_rate !== rate) && (
                                            <button
                                                key={rate}
                                                type="button"
                                                onClick={() => setPreview(prev => {
                                                    const copy = JSON.parse(JSON.stringify(prev));
                                                    copy.invoices[invIdx].items.forEach(it => { it.vat_rate = rate; });
                                                    return copy;
                                                })}
                                                className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 text-[11px] font-semibold"
                                                title={`Appliquer une TVA de ${String(rate).replace('.', ',')} % à toutes les lignes de cette facture`}
                                                data-testid={`vat-all-${String(rate).replace('.', '')}-${invIdx}`}
                                            >
                                                TVA {String(rate).replace('.', ',')} % partout
                                            </button>
                                        ))}
                                        {inv.already_imported && (
                                            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-semibold">Déjà importée</span>
                                        )}
                                    </div>
                                    {inv.client_name && (
                                        <div className={`flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs border-b ${inv.client_mismatch ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`} data-testid={`invoice-client-${invIdx}`}>
                                            <span className="text-slate-500">Client détecté :</span>
                                            <span className="font-semibold text-slate-700">{inv.client_name}</span>
                                            {inv.client_mismatch ? (
                                                <>
                                                    <span className="text-red-600 font-semibold flex items-center gap-1">
                                                        <AlertCircle className="w-3.5 h-3.5" />
                                                        Cette facture semble adressée à {inv.client_site_name}, vous avez sélectionné {preview.site_name}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSiteId(inv.client_site_id);
                                                            setPreview(prev => {
                                                                const copy = JSON.parse(JSON.stringify(prev));
                                                                copy.site_id = inv.client_site_id;
                                                                copy.site_name = inv.client_site_name;
                                                                copy.invoices.forEach(v => { v.client_mismatch = v.client_site_id ? v.client_site_id !== inv.client_site_id : false; });
                                                                return copy;
                                                            });
                                                            toast.success(`Site d'import changé : ${inv.client_site_name}`);
                                                        }}
                                                        className="px-2 py-0.5 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                                                        data-testid={`fix-site-${invIdx}`}
                                                    >
                                                        Importer vers {inv.client_site_name}
                                                    </button>
                                                </>
                                            ) : inv.client_site_id ? (
                                                <span className="text-emerald-600 font-semibold flex items-center gap-1">
                                                    <CheckCircle2 className="w-3.5 h-3.5" /> site confirmé
                                                </span>
                                            ) : (
                                                <span className="text-slate-400 italic">client non associé — sera mémorisé pour ce site à l'import</span>
                                            )}
                                        </div>
                                    )}
                                    <div className="overflow-x-auto max-w-full" style={{ WebkitOverflowScrolling: 'touch' }}>
                                    <table className="w-full text-xs">
                                        <thead className="text-slate-500">
                                            <tr>
                                                <th className="text-left px-3 py-1">Réf.</th>
                                                <th className="text-left px-3 py-1">Produit</th>
                                                <th className="text-right px-3 py-1">TVA</th>
                                                <th className="text-right px-3 py-1">PU HT</th>
                                                <th className="text-right px-3 py-1">Qté</th>
                                                <th className="text-right px-3 py-1">Total HT</th>
                                                <th className="text-center px-3 py-1">Statut</th>
                                                {isCarrefour ? (
                                                    <th className="text-center px-3 py-1">Créer ?</th>
                                                ) : (
                                                    <th className="text-left px-3 py-1">Lier à…</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inv.items.map((it, j) => {
                                                const key = `${invIdx}-${j}`;
                                                const manualSpId = manualMaps[key];
                                                const effectiveSpId = manualSpId || it.matched_supplier_product_id;
                                                return (
                                                    <tr key={j} className="border-t">
                                                        <td className="px-3 py-1 font-mono text-slate-500">{it.product_ref}</td>
                                                        <td className="px-3 py-1">
                                                            {it.matched_product_name ? (
                                                                <>
                                                                    <div className="text-emerald-700">{it.matched_product_name}</div>
                                                                    <div className="text-[10px] text-slate-400">{it.description}</div>
                                                                </>
                                                            ) : (
                                                                <span>{it.description}</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-1 text-right">
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                value={it.vat_rate ?? ''}
                                                                onChange={(e) => {
                                                                    const v = e.target.value === '' ? null : parseFloat(e.target.value);
                                                                    setPreview(prev => {
                                                                        const copy = JSON.parse(JSON.stringify(prev));
                                                                        copy.invoices[invIdx].items[j].vat_rate = v;
                                                                        return copy;
                                                                    });
                                                                }}
                                                                className={`w-14 text-right border rounded px-1 py-0.5 text-xs ${![0, 2.1, 8.5].includes(it.vat_rate) ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-slate-200'}`}
                                                                data-testid={`vat-input-${invIdx}-${j}`}
                                                            />%
                                                        </td>
                                                        <td className="px-3 py-1 text-right">{formatCurrency(it.unit_price_ht)}</td>
                                                        <td className="px-3 py-1 text-right font-semibold">
                                                            {(() => {
                                                                const isVW = !isCarrefour && effectiveSpId && spById[effectiveSpId]?.variable_weight;
                                                                if (!isVW) return it.quantity;
                                                                const ok = !!vwConfirms[key];
                                                                return (
                                                                    <div className="flex flex-col items-end gap-0.5">
                                                                        <div className={`flex items-center gap-1 border rounded px-1 py-0.5 ${ok ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}>
                                                                            <Scale className={`w-3 h-3 ${ok ? 'text-emerald-600' : 'text-amber-600'}`} />
                                                                            <input
                                                                                type="number" step="0.001" inputMode="decimal"
                                                                                value={it.quantity}
                                                                                onChange={(e) => { setItemQty(invIdx, j, e.target.value); setVwConfirms(prev => ({ ...prev, [key]: true })); }}
                                                                                className="w-20 text-right bg-white border border-slate-200 rounded px-1 py-0.5 text-xs"
                                                                                data-testid={`vw-qty-input-${invIdx}-${j}`}
                                                                            />
                                                                        </div>
                                                                        {!ok && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setVwConfirms(prev => ({ ...prev, [key]: true }))}
                                                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700"
                                                                                title={`Accepter le poids manuscrit lu par l'IA : ${it.quantity}`}
                                                                                data-testid={`vw-accept-${invIdx}-${j}`}
                                                                            >
                                                                                <CheckCircle2 className="w-3 h-3" /> Accepter
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                            {it.weighed_suggestion && (
                                                                <div className="text-[10px] text-orange-600 font-normal italic">
                                                                    → {it.weighed_suggestion.quantity_corrected} kg
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-1 text-right font-semibold">{formatCurrency(it.total_line_ht)}</td>
                                                        <td className="px-3 py-1 text-center">
                                                            <div className="flex flex-col items-center gap-0.5">
                                                                {statusBadge(it.match_status)}
                                                                {it.weighed_suggestion && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => applyWeightCorrection(invIdx, j)}
                                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-100 hover:bg-orange-200 text-orange-700 text-[10px] font-semibold"
                                                                        title={`Corriger : ${it.weighed_suggestion.quantity_corrected} kg × ${it.weighed_suggestion.pu_ref} €`}
                                                                        data-testid={`apply-weight-${invIdx}-${j}`}
                                                                    >
                                                                        <Scale className="w-3 h-3" />
                                                                        Pesée caisse auto
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-1">
                                                            {isCarrefour ? (
                                                                !it.matched_supplier_product_id && (
                                                                    <div className="flex justify-center">
                                                                        <Checkbox
                                                                            checked={!!createFlags[key]}
                                                                            onCheckedChange={(v) => setCreateFlags(prev => ({ ...prev, [key]: !!v }))}
                                                                        />
                                                                    </div>
                                                                )
                                                            ) : (
                                                                // Generic: show dropdown when not exact, or to override
                                                                (it.match_status !== 'exact' || manualSpId) ? (
                                                                    <Select
                                                                        value={effectiveSpId || '__none__'}
                                                                        onValueChange={(v) => setManualMaps(prev => ({ ...prev, [key]: v === '__none__' ? null : v }))}
                                                                    >
                                                                        <SelectTrigger className="h-7 text-[11px]" data-testid={`map-line-${invIdx}-${j}`}>
                                                                            <SelectValue placeholder="Sélectionner un produit…" />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="__none__">— Ignorer cette ligne —</SelectItem>
                                                                            {supplierProductOptions.map(sp => (
                                                                                <SelectItem key={sp.id} value={sp.id}>
                                                                                    {sp.supplier_name_product || sp.supplier_ref}
                                                                                    {sp.supplier_ref && <span className="text-slate-400 ml-1">({sp.supplier_ref})</span>}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                ) : (
                                                                    <span className="text-[10px] text-slate-400 italic">auto</span>
                                                                )
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <DialogFooter className="flex-shrink-0 border-t pt-3">
                            <Button variant="outline" onClick={handleClose}>Annuler</Button>
                            <Button variant="outline" onClick={reset}>
                                <X className="w-4 h-4 mr-2" />Changer de PDF
                            </Button>
                            {isCarrefour && preview.invoices.some(inv => inv.items.some(it => it.weighed_suggestion)) && (
                                <Button
                                    variant="outline"
                                    onClick={applyAllWeightCorrections}
                                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                    data-testid="correct-all-weights-btn"
                                >
                                    <Scale className="w-4 h-4 mr-2" />
                                    Corriger les pesées caisse auto
                                </Button>
                            )}
                            <Button
                                onClick={handleConfirm}
                                disabled={selectedCount === 0 || loading}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                data-testid="confirm-import-btn"
                            >
                                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Import…</> : <><CheckCircle2 className="w-4 h-4 mr-2" />Importer {selectedCount} facture(s)</>}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
